/**
 * The payroll (contract v10.53, ADR 0047 of the back). Every call is for whoever has
 * `can_send_payroll`; the API refuses the rest with 403 (`X-Cubas-Reason: payroll`).
 */
import { api, type Page } from '@/shared/api/client';
import { apiUrl } from '@/shared/config';

import type {
  PayrollBatch,
  PayrollBatchDetail,
  PayrollEmployee,
  PayrollEmployeeCreated,
  PayrollEmployeeUpdate,
  PayrollEmployeeWrite,
  PayrollImportResult,
  PayrollPasswordIssued,
  PayrollStatus,
} from './types';

export interface EmployeeListParams {
  q?: string;
  include_inactive?: boolean;
}

export const payrollKeys = {
  all: ['payroll'] as const,
  status: ['payroll', 'status'] as const,
  batches: ['payroll', 'batches'] as const,
  batchList: (offset: number) => ['payroll', 'batches', 'list', offset] as const,
  batch: (id: string) => ['payroll', 'batches', 'detail', id] as const,
  employees: ['payroll', 'employees'] as const,
  employeeList: (params: EmployeeListParams) => ['payroll', 'employees', 'list', params] as const,
};

export const BATCH_PAGE = 50;

export const payrollApi = {
  status: (): Promise<PayrollStatus> => api.get<PayrollStatus>('/api/payroll'),

  batches: (offset: number): Promise<Page<PayrollBatch>> =>
    api.get<Page<PayrollBatch>>('/api/payroll/batches', { limit: BATCH_PAGE, offset }),

  batch: (id: string): Promise<PayrollBatchDetail> =>
    api.get<PayrollBatchDetail>(`/api/payroll/batches/${id}`),

  upload: (file: File): Promise<PayrollBatchDetail> => {
    const form = new FormData();
    form.append('file', file);
    return api.postForm<PayrollBatchDetail>('/api/payroll/batches', form);
  },

  givePassword: (id: string, password: string, remember: boolean): Promise<PayrollBatchDetail> =>
    api.post<PayrollBatchDetail>(`/api/payroll/batches/${id}/password`, { password, remember }),

  setPeriod: (id: string, period: string): Promise<PayrollBatchDetail> =>
    api.patch<PayrollBatchDetail>(`/api/payroll/batches/${id}`, { period }),

  accept: (id: string): Promise<PayrollBatchDetail> =>
    api.post<PayrollBatchDetail>(`/api/payroll/batches/${id}/accept`),

  discard: (id: string): Promise<PayrollBatchDetail> =>
    api.post<PayrollBatchDetail>(`/api/payroll/batches/${id}/discard`),

  retry: (id: string): Promise<PayrollBatchDetail> =>
    api.post<PayrollBatchDetail>(`/api/payroll/batches/${id}/retry`),

  /** `employee_id: null` takes the person off; `excluded` is «No enviar». */
  updateSlip: (
    id: string,
    slipId: string,
    change: { employee_id?: string | null; excluded?: boolean },
  ): Promise<PayrollBatchDetail> =>
    api.patch<PayrollBatchDetail>(`/api/payroll/batches/${id}/slips/${slipId}`, change),

  /**
   * The payslip's PDF, not encrypted, to look at: a link opened in a new tab, which the
   * browser shows with its own viewer (`inline`, `no-store`). Not a download, so no copy
   * stays in the computer's Downloads; and not a `blob:` in this tab, which the CSP
   * (`object-src 'none'`) would not let the viewer open. The session cookie goes with
   * the navigation (same site). Each opening is in the access log.
   */
  slipPdfUrl: (id: string, slipId: string): string =>
    `${apiUrl}/api/payroll/batches/${id}/slips/${slipId}/pdf`,

  test: (id: string): Promise<PayrollBatchDetail> =>
    api.post<PayrollBatchDetail>(`/api/payroll/batches/${id}/test`),

  /** Elevated: through `withElevation`. `count` is the number the person was shown. */
  send: (id: string, count: number): Promise<PayrollBatchDetail> =>
    api.post<PayrollBatchDetail>(`/api/payroll/batches/${id}/send`, { count }),

  employees: (params: EmployeeListParams): Promise<Page<PayrollEmployee>> =>
    api.get<Page<PayrollEmployee>>('/api/payroll/employees', { ...params, limit: 200 }),

  createEmployee: (payload: PayrollEmployeeWrite): Promise<PayrollEmployeeCreated> =>
    api.post<PayrollEmployeeCreated>('/api/payroll/employees', payload),

  updateEmployee: (id: string, payload: PayrollEmployeeUpdate): Promise<PayrollEmployee> =>
    api.patch<PayrollEmployee>(`/api/payroll/employees/${id}`, payload),

  /** Elevated: through `withElevation`. The password comes in this answer only. */
  newPassword: (id: string): Promise<PayrollPasswordIssued> =>
    api.post<PayrollPasswordIssued>(`/api/payroll/employees/${id}/password`),

  importEmployees: (file: File): Promise<PayrollImportResult> => {
    const form = new FormData();
    form.append('file', file);
    return api.postForm<PayrollImportResult>('/api/payroll/employees/import', form);
  },
};
